import { Check, ChevronDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** One slicer, many picks. The value is a comma-joined string, so a view is still a plain link.
 *
 * With `counts` in hand the slicer is **data-driven**: it offers only what is still there to be
 * found given every other pick, and says how many each one holds. A pick that would bring nothing
 * back is never offered.
 */
export const MultiSelect = ({
  label,
  options = [],
  value = "",
  onChange,
  testId,
  width = "w-[180px]",
  counts = null,
  always = [],
}) => {
  const picked = String(value || "")
    .split(",")
    .filter(Boolean);
  const toggle = (v) =>
    onChange(
      (picked.includes(v) ? picked.filter((x) => x !== v) : [...picked, v]).join(","),
    );
  // `always` is for an option that is logically right for what is already picked even though no
  // row stands there yet — the sub-stages of a status, for instance. It is offered with its nought
  // beside it rather than hidden, so an officer can see the whole road, not only the trodden part.
  const live = counts
    ? options.filter(
        (o) => (counts[o.value] || 0) > 0 || picked.includes(o.value) || always.includes(o.value),
      )
    : options;
  const shown =
    picked.length === 0
      ? label
      : picked.length === 1
        ? options.find((o) => o.value === picked[0])?.label || picked[0]
        : `${picked.length} picked`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`flex h-11 ${width} items-center justify-between gap-2 border px-3 text-left text-sm transition-colors duration-200 ${
            picked.length
              ? "border-primary bg-card text-primary"
              : "border-border bg-card hover:bg-muted"
          }`}
        >
          <span className="truncate">{shown}</span>
          {picked.length ? (
            <X
              className="h-3.5 w-3.5 shrink-0 opacity-70"
              data-testid={`${testId}-clear`}
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
            />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-80 w-72 overflow-y-auto rounded-none bg-popover p-0"
      >
        <div className="border-b border-border px-3 py-2">
          <div className="label-caps">{label}</div>
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            {counts
              ? "Only what your other picks still leave open — the figure is how many it holds."
              : "Pick as many as you need — any of them matches."}
          </p>
        </div>
        {live.map((o) => {
          const on = picked.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              data-testid={`${testId}-option-${o.value}`}
              onClick={() => toggle(o.value)}
              className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors duration-200 hover:bg-muted"
            >
              <span
                className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center border ${
                  on ? "border-primary bg-primary text-primary-foreground" : "border-border"
                }`}
              >
                {on && <Check className="h-3 w-3" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className={on ? "font-semibold" : ""}>{o.label || o.value}</span>
                {counts && (
                  <span className="font-data ml-1.5 text-[11px] tabular-nums text-muted-foreground">
                    · {counts[o.value] || 0}
                  </span>
                )}
                {o.hint && (
                  <span className="block text-[10px] text-muted-foreground">{o.hint}</span>
                )}
              </span>
            </button>
          );
        })}
        {!live.length && (
          <p className="px-3 py-4 text-xs text-muted-foreground" data-testid={`${testId}-empty`}>
            {counts && options.length
              ? "Nothing left here — what you have already picked rules every one of these out."
              : "Nothing to choose from yet."}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
};
